import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Loader2, Save,
  GripVertical, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const FIELD_TYPES = [
  { value: 'short_text', label: 'Short answer' },
  { value: 'long_text', label: 'Paragraph' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'radio', label: 'Multiple choice' },
  { value: 'checkboxes', label: 'Checkboxes' },
  { value: 'yes_no', label: 'Yes / No' },
];

const MAPS_TO = [
  { value: 'none', label: '— Not mapped (stored only) —' },
  { value: 'full_name', label: 'Full Name' },
  { value: 'email', label: 'Email (required for waitlist)' },
  { value: 'phone', label: 'Phone' },
  { value: 'tshirt_size', label: 'T-Shirt Size' },
  { value: 'room_preference', label: 'Room Preference' },
  { value: 'dietary', label: 'Dietary Restrictions' },
  { value: 'accessibility', label: 'Accessibility Needs' },
  { value: 'commitment', label: 'Full-Retreat Commitment' },
  { value: 'financial_assistance', label: 'Financial Assistance' },
  { value: 'service', label: 'Willing to Serve' },
  { value: 'comments', label: 'Notes / Comments' },
];

const HAS_OPTIONS = ['dropdown', 'radio', 'checkboxes'];
const newFieldId = () => Math.random().toString(36).slice(2, 12);

const FieldEditor = ({ field, index, total, expanded, onToggle, onChange, onMove, onDelete }) => {
  const set = (key, value) => onChange({ ...field, [key]: value });
  return (
    <Card className="border border-gray-200" data-testid={`field-card-${index}`}>
      <CardContent className="p-0">
        <button type="button" onClick={onToggle}
          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
          data-testid={`field-toggle-${index}`}>
          <GripVertical className="h-4 w-4 text-gray-300 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {field.label || <span className="text-gray-400 italic">Untitled question</span>}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </p>
            <p className="text-xs text-gray-400">
              {FIELD_TYPES.find(t => t.value === field.type)?.label}
              {field.maps_to !== 'none' && ` → ${MAPS_TO.find(m => m.value === field.maps_to)?.label?.split(' (')[0]}`}
            </p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <Button size="sm" variant="ghost" disabled={index === 0} onClick={() => onMove(-1)}
              className="h-7 w-7 p-0" data-testid={`field-up-${index}`}>
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" disabled={index === total - 1} onClick={() => onMove(1)}
              className="h-7 w-7 p-0" data-testid={`field-down-${index}`}>
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={onDelete}
              className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" data-testid={`field-delete-${index}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ${expanded ? 'rotate-90' : ''}`} />
        </button>

        {expanded && (
          <div className="px-4 pb-4 pt-1 border-t border-gray-100 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Question</label>
                <Input value={field.label} onChange={(e) => set('label', e.target.value)}
                  placeholder="e.g. What's your t-shirt size?" className="mt-1" data-testid={`field-label-${index}`} />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Type</label>
                <select value={field.type} onChange={(e) => set('type', e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wine"
                  data-testid={`field-type-${index}`}>
                  {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            {HAS_OPTIONS.includes(field.type) && (
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Options (one per line)</label>
                <Textarea value={(field.options || []).join('\n')}
                  onChange={(e) => set('options', e.target.value.split('\n'))}
                  onBlur={(e) => set('options', e.target.value.split('\n').map(o => o.trim()).filter(Boolean))}
                  rows={4} placeholder={'Option 1\nOption 2'} className="mt-1 font-mono text-sm"
                  data-testid={`field-options-${index}`} />
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Placeholder (optional)</label>
                <Input value={field.placeholder || ''} onChange={(e) => set('placeholder', e.target.value)}
                  className="mt-1" data-testid={`field-placeholder-${index}`} />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Help text (optional)</label>
                <Input value={field.help_text || ''} onChange={(e) => set('help_text', e.target.value)}
                  className="mt-1" data-testid={`field-helptext-${index}`} />
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1">
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Saves to attendee field</label>
                <select value={field.maps_to || 'none'} onChange={(e) => set('maps_to', e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wine"
                  data-testid={`field-mapsto-${index}`}>
                  {MAPS_TO.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <p className="text-[11px] text-gray-400 mt-1">Mapped answers auto-fill the waitlist entry. Unmapped answers are still stored with the submission.</p>
              </div>
              <div className="flex items-center gap-2 md:pt-5">
                <Switch checked={!!field.required} onCheckedChange={(v) => set('required', v)}
                  data-testid={`field-required-${index}`} />
                <span className="text-sm text-gray-700 font-medium">Required</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const FormBuilder = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchForm = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/retreat/forms/${formId}`, { headers: getAuthHeaders() });
      setForm(res.data);
    } catch {
      toast.error('Failed to load form');
      navigate('/admin/retreat/forms');
    } finally {
      setLoading(false);
    }
  }, [formId, navigate]);

  useEffect(() => { fetchForm(); }, [fetchForm]);

  const update = (changes) => { setForm(f => ({ ...f, ...changes })); setDirty(true); };

  const updateField = (idx, newField) => {
    const fields = [...form.fields];
    fields[idx] = newField;
    update({ fields });
  };

  const moveField = (idx, dir) => {
    const fields = [...form.fields];
    const [item] = fields.splice(idx, 1);
    fields.splice(idx + dir, 0, item);
    update({ fields });
  };

  const deleteField = (idx) => {
    if (!window.confirm(`Delete question "${form.fields[idx].label || 'Untitled'}"?`)) return;
    update({ fields: form.fields.filter((_, i) => i !== idx) });
  };

  const addField = () => {
    const field = { id: newFieldId(), label: '', type: 'short_text', required: false, options: [], placeholder: '', help_text: '', maps_to: 'none' };
    update({ fields: [...form.fields, field] });
    setExpandedId(field.id);
  };

  const save = async () => {
    const empty = form.fields.find(f => !f.label.trim());
    if (empty) { toast.error('Every question needs a label before saving'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        success_message: form.success_message,
        fields: form.fields.map(f => ({ ...f, options: (f.options || []).map(o => o.trim()).filter(Boolean) })),
      };
      const res = await axios.put(`${BACKEND_URL}/api/retreat/forms/${formId}`, payload, { headers: getAuthHeaders() });
      setForm(res.data);
      setDirty(false);
      toast.success('Form saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-wine" /></div>;
  }

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="form-builder-page">
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/retreat/forms')} data-testid="back-to-forms-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> All Forms
        </Button>
        <div className="flex items-center gap-3">
          {form.is_live && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-300">
              <span className="h-2 w-2 rounded-full bg-emerald-600 animate-pulse" /> LIVE
            </span>
          )}
          <Button onClick={save} disabled={saving || !dirty} data-testid="save-form-btn"
            className="bg-gradient-to-r from-wine to-burgundy text-white hover:opacity-90">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {dirty ? 'Save Changes' : 'Saved'}
          </Button>
        </div>
      </div>

      <Card className="mb-6 border-t-4 border-t-wine">
        <CardContent className="p-5 md:p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Form Title</label>
            <Input value={form.title} onChange={(e) => update({ title: e.target.value })}
              className="mt-1 text-lg font-semibold" data-testid="form-title-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Description (shown above the form)</label>
            <Textarea value={form.description || ''} onChange={(e) => update({ description: e.target.value })}
              rows={3} className="mt-1" data-testid="form-description-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Success message (shown after submitting)</label>
            <Textarea value={form.success_message || ''} onChange={(e) => update({ success_message: e.target.value })}
              rows={2} className="mt-1" data-testid="form-success-input" />
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Questions ({form.fields.length})</h2>
        <Button size="sm" variant="outline" onClick={addField} data-testid="add-field-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add Question
        </Button>
      </div>

      <div className="space-y-3">
        {form.fields.map((field, idx) => (
          <FieldEditor key={field.id} field={field} index={idx} total={form.fields.length}
            expanded={expandedId === field.id}
            onToggle={() => setExpandedId(expandedId === field.id ? null : field.id)}
            onChange={(f) => updateField(idx, f)}
            onMove={(dir) => moveField(idx, dir)}
            onDelete={() => deleteField(idx)} />
        ))}
        {form.fields.length === 0 && (
          <Card><CardContent className="py-10 text-center text-gray-400 text-sm">No questions yet — add your first one.</CardContent></Card>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={saving || !dirty} data-testid="save-form-btn-bottom"
          className="bg-gradient-to-r from-wine to-burgundy text-white hover:opacity-90">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          {dirty ? 'Save Changes' : 'Saved'}
        </Button>
      </div>
    </div>
  );
};

export default FormBuilder;
